import type User from '@/models/user/User';
import type { AppDomainKey } from '@/env/AppDomain';

export type NotificationId = string;

/**
 * Per-user "seen" state persisted in UiStorage under {@link NotificationStorageService.STORAGE_KEY}.
 * Maps a notification id to `true` once the user has acknowledged it (unseen ids are simply absent).
 */
export type NotificationsSeenState = Record<NotificationId, boolean>;

export enum NotificationScope {
  /** Evaluated once after login, when the CRM shell mounts. */
  GLOBAL = 'GLOBAL',
  /** Evaluated on first entry to one of {@link NotificationDescriptor.routeNames}. */
  ROUTE = 'ROUTE',
}

export interface NotificationDescriptor {
  /**
   * Stable, unique id, e.g. `'bik-redesign-2026-06'`. Shown once per user; reusing an id keeps it
   * suppressed for users who saw it (use a NEW id to show revised content again).
   */
  id: NotificationId;
  scope: NotificationScope;
  /** Required when `scope === ROUTE`. Values are route `name`s (see `@/router/Routes`). */
  routeNames?: string[];
  /** Target tenant(s). Omit to target both LENDI_PL and NESTO_RO. */
  tenants?: AppDomainKey[];
  /** Optional role/condition gate, evaluated against the logged-in user (e.g. `isExpert`). */
  audience?: (user: User) => boolean;
  /** Higher shows first when several are eligible at the same trigger. Defaults to `0`. */
  priority?: number;
  /**
   * Optional CSS selector of an on-screen element. When set (and the element exists), the
   * notification is shown as a tutorial-style speech bubble anchored to — and highlighting —
   * that element (vue-tour), instead of a centered modal. E.g. `'.LEAD-GO_TO_DEALS'` for the
   * deals tab in the side menu. Falls back to the centered modal if the element is not found.
   */
  anchorSelector?: string;
  /** Bubble placement relative to the anchor (vue-tour). Defaults to `'right'`. Ignored without an anchor. */
  anchorPlacement?: 'top' | 'bottom' | 'left' | 'right';
  // Copy is provided as lazy getters so I18NGetter() resolves the CURRENT SystemLanguage at show time.
  // NEVER pass eager strings here — `() => I18NGetter().usePlatformNotifications.X`, not the resolved value.
  title: () => string;
  description: () => string;
  /** Button label. Both modal buttons just dismiss, so this is the only label needed. */
  trueText: () => string;
}
